import { useMemo } from "react";

import { DATE_FORMATS } from "@bps/date-time";
import {
  DefaultButton,
  DetailsRow,
  IColumn,
  ScrollablePane,
  ShimmeredDetailsListProps,
  Text
} from "@bps/fluent-ui";
import { ShimmeredDetailsList } from "@components/tables/ShimmeredDetailsList";
import { usePermissionsCheckQuery } from "@libs/api/gateways/env/env-gateway.hooks";
import { toDateTime } from "@libs/common/common.utils";
import { NO_ACTION_PERMISSION } from "@libs/permissions/permissions.constants";
import { Permissions } from "@libs/permissions/permissions.enum";

import { ConfigValueInput } from "./data-types";
import { RolloutPackageConfigurationDto } from "./EditRolloutPackageDesiredConfig";

export interface RolloutPackageDesiredConfigTableProps
  extends ShimmeredDetailsListProps {
  isReadOnly: boolean;
  onUpdateRolloutPackageDesiredConfig: (
    key: string,
    value: string | undefined
  ) => void;
  onDeleteRolloutPackageDesiredConfig: (index: number) => void;
}

export const RolloutPackageDesiredConfigTable = ({
  isReadOnly,
  onUpdateRolloutPackageDesiredConfig,
  onDeleteRolloutPackageDesiredConfig,
  ...props
}: RolloutPackageDesiredConfigTableProps) => {
  const { data: hasDeployPermission } = usePermissionsCheckQuery(
    Permissions.PltFieldManagementDeploy
  );

  const columns: IColumn[] = useMemo(() => {
    return [
      {
        key: "key",
        name: "Key",
        minWidth: 100,
        maxWidth: 350,
        isResizable: true,
        onRender: (dto: RolloutPackageConfigurationDto) => (
          <Text
            styles={{
              root: { whiteSpace: "initial" }
            }}
          >
            {dto.key}
          </Text>
        )
      },
      {
        key: "value",
        name: "Value",
        minWidth: 150,
        maxWidth: 300,
        isResizable: true,
        onRender: (dto: RolloutPackageConfigurationDto) => (
          <ConfigValueInput
            isReadOnly={isReadOnly}
            dataType={dto.dataType}
            value={dto.value}
            onUpdate={val =>
              dto && onUpdateRolloutPackageDesiredConfig(dto.key, val!)
            }
          />
        )
      },
      {
        key: "defaultValue",
        name: "Default Value",
        minWidth: 150,
        maxWidth: 300,
        isResizable: true,
        onRender: (dto: RolloutPackageConfigurationDto) => (
          <ConfigValueInput
            isReadOnly={true}
            dataType={dto.dataType}
            value={dto.defaultValue}
            onUpdate={() => {}}
          />
        )
      },
      {
        key: "setDefault",
        name: "",
        minWidth: 150,
        maxWidth: 150,
        isResizable: true,
        onRender: (dto: RolloutPackageConfigurationDto, index?: number) => {
          const cannotUpdate = dto.value === dto.defaultValue || isReadOnly;

          return (
            <DefaultButton
              disabled={cannotUpdate || !hasDeployPermission}
              onClick={() =>
                index !== undefined &&
                onDeleteRolloutPackageDesiredConfig(index)
              }
              title={
                !hasDeployPermission && !cannotUpdate
                  ? NO_ACTION_PERMISSION
                  : undefined
              }
            >
              Set to Default
            </DefaultButton>
          );
        }
      },
      {
        key: "description",
        name: "Description",
        minWidth: 200,
        maxWidth: 500,
        isResizable: true,
        onRender: (dto: RolloutPackageConfigurationDto) => (
          <Text
            variant="small"
            styles={{
              root: { whiteSpace: "initial" }
            }}
          >
            {dto.description}
          </Text>
        )
      },
      {
        key: "updatedDate",
        name: "Last Updated",
        minWidth: 150,
        isResizable: true,
        onRender: (dto: RolloutPackageConfigurationDto) => (
          <Text>
            {dto.changeLog?.updatedDate
              ? toDateTime(dto.changeLog?.updatedDate)!.toFormat(
                  DATE_FORMATS.LONG_DATE_TIME_FORMAT
                )
              : ""}
          </Text>
        )
      }
    ];
  }, [
    hasDeployPermission,
    onDeleteRolloutPackageDesiredConfig,
    onUpdateRolloutPackageDesiredConfig,
    isReadOnly
  ]);

  return (
    <ScrollablePane styles={{ root: { height: 500, position: "relative" } }}>
      <ShimmeredDetailsList
        stickyHeader
        shimmerLines={5}
        columns={columns}
        onShouldVirtualize={() => true}
        onRenderRow={row => (row ? <DetailsRow {...row} /> : null)}
        selectionPreservedOnEmptyClick
        setKey="rollout-package-desired-config-table"
        {...props}
      />
    </ScrollablePane>
  );
};
